import api, { createApiWithNewBasePath } from "@api";
import { components } from "@api/schema";
import { ID } from "@types";

export const API_PATH = "service_providers";
export const API_PATH_LEVEL_UP_INFO = "service_providers/level_up_info";

export interface ILevelUpInfoItem {
  key: string;
  state: string;
  optional?: boolean;
}
export const API_PROVIDER_SERVICES_COUNT = "services_count";
export const API_PATH_METRICS = "service_provider_metrics";
export const API_PATH_REPEAT_RATE = "repeat_rate";
export const API_PATH_REALISATION_RATE = "realisation_rate";
export const API_PATH_ADVANTAGE_LEVEL = "advantage_level";
export const API_PATH_SELECTION_RATE = "selection_rate";
export const API_PATH_PROFILE_PICTURE_VALIDATION = "profile_picture_validation";

export interface ISelectionRateDeclineReason {
  reason: string;
  count: number;
}

export interface IServiceProviderSelectionRateMetrics {
  name: string;
  value: number | null;
  threshold: number;
  currentLevel?: string;
  remainingEffort?: number | null;
  computationDetails: {
    offersSentCount: number;
    selectionsCount: number;
  };
  declineReasons: ISelectionRateDeclineReason[];
}

interface IRawSelectionRateMetricsResponse {
  data: {
    declined_reasons: ISelectionRateDeclineReason[];
    metric: {
      name: string;
      value: number | null;
      threshold: number;
      current_level?: string;
      computation_details: {
        offers_sent_count: number;
        selections_count: number;
      };
    };
    remaining_effort: number | null;
  };
}

export type TAdvantageLevel = "standard" | "good" | "great" | "super_talent";

export type TNewbieBoostStatus = "applicable" | "active";

export interface IAdvantageLevel {
  advantageLevel: TAdvantageLevel;
  pro: boolean;
  newbieBoostStatus: TNewbieBoostStatus | null;
}

export type IServiceProvider = components["schemas"]["serviceProvider"];

export type IProviderServicesCount =
  components["schemas"]["serviceProviderServicesCount"];

export type IServiceProviderMetrics =
  components["schemas"]["serviceProviderMetrics"];

export type IServiceProviderRepeatRateMetrics =
  components["schemas"]["serviceProviderRepeatRateMetric"] & { remainingEffort?: number | null };

export type IServiceProviderRealisationRateMetrics =
  components["schemas"]["serviceProviderRealisationRateMetric"] & { remainingEffort?: number | null };
export interface IGetServiceProviderOptions {
  includes?: "user"[];
}

export const getServiceProvider = async (
  id: ID,
  options?: IGetServiceProviderOptions
) => {
  return await api.get<IGetServiceProviderOptions, IServiceProvider>(
    [API_PATH, String(id)].join("/"),
    options
  );
};

export const getProviderServicesCount = async (
  id: ID,
  options?: IGetServiceProviderOptions
) => {
  return await api.get<IGetServiceProviderOptions, IProviderServicesCount>(
    [API_PATH, String(id), API_PROVIDER_SERVICES_COUNT].join("/"),
    options
  );
};

export const getServiceProviderMetrics = (
  id: ID,
  options?: IGetServiceProviderOptions
) => {
  return api.get<IGetServiceProviderOptions, IServiceProviderMetrics>(
    [API_PATH_METRICS, String(id)].join("/"),
    options
  );
};

export const getRepeatRateMetrics = (
  id: ID,
  options?: IGetServiceProviderOptions
) => {
  return api.get<IGetServiceProviderOptions, IServiceProviderRepeatRateMetrics>(
    [API_PATH_METRICS, String(id), API_PATH_REPEAT_RATE].join("/"),
    options
  );
};

export const getLevelUpInfo = () => {
  return createApiWithNewBasePath.get<undefined, ILevelUpInfoItem[]>(API_PATH_LEVEL_UP_INFO);
};

export const getSelectionRateMetrics = async (id: ID) => {
  const response = await createApiWithNewBasePath.get<
    undefined,
    IRawSelectionRateMetricsResponse
  >([API_PATH_METRICS, String(id), API_PATH_SELECTION_RATE].join("/"));

  const { metric, declined_reasons, remaining_effort } = response.data.data;

  return {
    ...response,
    data: {
      name: metric.name,
      value: metric.value,
      threshold: metric.threshold,
      currentLevel: metric.current_level,
      remainingEffort: remaining_effort,
      computationDetails: {
        offersSentCount: metric.computation_details.offers_sent_count,
        selectionsCount: metric.computation_details.selections_count,
      },
      declineReasons: declined_reasons,
    } as IServiceProviderSelectionRateMetrics,
  };
};

export const getServiceProviderAdvantageLevel = (id: ID) => {
  return api.get<undefined, IAdvantageLevel>(
    [API_PATH_METRICS, String(id), API_PATH_ADVANTAGE_LEVEL].join("/")
  );
};

export const getRealisationRateMetrics = (
  id: ID,
  options?: IGetServiceProviderOptions
) => {
  return api.get<
    IGetServiceProviderOptions,
    IServiceProviderRealisationRateMetrics
  >(
    [API_PATH_METRICS, String(id), API_PATH_REALISATION_RATE].join("/"),
    options
  );
};

export const requestProfilePictureValidation = (id: ID) => {
  return createApiWithNewBasePath.post<undefined, void>(
    [API_PATH, String(id), API_PATH_PROFILE_PICTURE_VALIDATION].join("/")
  );
};
